import React, { useState, useEffect } from 'react';
import {
  Trash2, RotateCcw, AlertTriangle, X, Loader2, Info, Users,
} from 'lucide-react';
import { Button } from '../../ui-v2';
import { cn } from '../../../../lib/cn';
import ConfirmacionEliminar from '../../ui/ConfirmacionEliminar';

/**
 * AccionesCampanaCancelada — botones + modales para el ciclo de vida
 * de una campaña en estado 'Cancelada'.
 *
 * Solo se renderiza si campana.estado === 'Cancelada'. Fuera de ese
 * estado, no aparece nada — el componente padre no necesita lógica
 * condicional.
 *
 * Props:
 *   campana: { id, estado, titulo, ... }
 *   onEliminada: () => void
 *     Callback tras eliminar (habitual: redirigir a la lista).
 *   onReactivada: (data) => void
 *     Callback tras reactivar. data incluye:
 *       { estado: 'Activa', aviso, participaciones_revividas }
 *
 *   eliminarCampana, cambiarEstadoCampana, contarParticipacionesCanceladasSistema:
 *     funciones API inyectadas (para no acoplar a un módulo concreto).
 *
 * Comportamiento:
 *   - Eliminar → modal ConfirmacionEliminar (doble seguro) + llamada + onEliminada.
 *   - Reactivar → modal con:
 *       · Info del riesgo (fecha límite pasada, si aplica)
 *       · Contador de participaciones canceladas por el sistema
 *       · Radio "revivir participaciones" (Sí/No)
 *       · Botón Reactivar
 */

export default function AccionesCampanaCancelada({
  campana,
  onEliminada,
  onReactivada,
  eliminarCampana,
  cambiarEstadoCampana,
  contarParticipacionesCanceladasSistema,
}) {

  const [eliminando, setEliminando] = useState(false);
  const [confirmandoEliminar, setConfirmandoEliminar] = useState(false);
  const [errorEliminar, setErrorEliminar] = useState(null);
  const [modalReactivar, setModalReactivar] = useState(false);

  if (!campana || campana.estado !== 'Cancelada') return null;

  const fechaLimitePasada = campana.fecha_limite &&
    new Date(campana.fecha_limite) < new Date();

  // Fase 17 — se unifica al patrón del proyecto: modal ConfirmacionEliminar
  // (doble seguro: escribir el título) en vez de window.confirm.
  async function ejecutarEliminacion() {
    setEliminando(true);
    setErrorEliminar(null);
    try {
      await eliminarCampana(campana.id);
      setConfirmandoEliminar(false);
      onEliminada?.();
    } catch (e) {
      console.error('[AccionesCampanaCancelada] eliminar', e);
      setErrorEliminar(e?.response?.data?.error || 'No se pudo eliminar la campaña.');
    } finally {
      setEliminando(false);
    }
  }

  return (
    <>
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => setModalReactivar(true)}
          className="inline-flex items-center gap-1.5 text-sm text-emerald-700 border border-emerald-200 hover:bg-emerald-50 px-3 py-1.5 rounded-md transition-colors"
          title="Reactivar campaña"
        >
          <RotateCcw className="w-3.5 h-3.5" />
          Reactivar
        </button>

        <button
          type="button"
          onClick={() => { setErrorEliminar(null); setConfirmandoEliminar(true); }}
          disabled={eliminando}
          className="inline-flex items-center gap-1.5 text-sm text-red-700 border border-red-200 hover:bg-red-50 px-3 py-1.5 rounded-md transition-colors disabled:opacity-50"
          title="Eliminar campaña definitivamente"
        >
          <Trash2 className="w-3.5 h-3.5" />
          Eliminar
        </button>
      </div>

      <ConfirmacionEliminar
        open={confirmandoEliminar}
        onClose={() => { setConfirmandoEliminar(false); setErrorEliminar(null); }}
        onConfirm={ejecutarEliminacion}
        titulo="Eliminar campaña"
        descripcion={errorEliminar
          ? errorEliminar
          : `Vas a eliminar definitivamente la campaña "${campana.titulo}". Esta acción es irreversible: se borrarán sus citas, participaciones de usuarios, asignaciones de técnicos y franjas asociadas.`}
        palabraConfirmacion={campana.titulo}
        textoBotonConfirmar="Eliminar definitivamente"
      />

      {modalReactivar && (
        <ModalReactivar
          campana={campana}
          fechaLimitePasada={fechaLimitePasada}
          onClose={() => setModalReactivar(false)}
          onReactivada={onReactivada}
          cambiarEstadoCampana={cambiarEstadoCampana}
          contarParticipacionesCanceladasSistema={contarParticipacionesCanceladasSistema}
        />
      )}
    </>
  );
}

// ────────────────────────────────────────────────────────────────
//  Modal de reactivación
// ────────────────────────────────────────────────────────────────

function ModalReactivar({
  campana,
  fechaLimitePasada,
  onClose,
  onReactivada,
  cambiarEstadoCampana,
  contarParticipacionesCanceladasSistema,
}) {

  const [revivir, setRevivir] = useState('si');   // 'si' | 'no'
  const [nCanceladas, setNCanceladas] = useState(null);
  const [loadingCount, setLoadingCount] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!contarParticipacionesCanceladasSistema) {
      setLoadingCount(false);
      return;
    }
    let cancelado = false;
    (async () => {
      try {
        const { count } = await contarParticipacionesCanceladasSistema(campana.id);
        if (!cancelado) setNCanceladas(count);
      } catch (e) {
        if (!cancelado) setNCanceladas(null);   // sin contador si falla
      } finally {
        if (!cancelado) setLoadingCount(false);
      }
    })();
    return () => { cancelado = true; };
  }, [campana.id, contarParticipacionesCanceladasSistema]);

  async function handleReactivar() {
    setGuardando(true);
    setError(null);
    try {
      const data = await cambiarEstadoCampana(campana.id, {
        estado: 'Activa',
        revivir_participaciones: revivir === 'si',
      });
      onReactivada?.(data);
      onClose();
    } catch (e) {
      console.error('[ModalReactivar]', e);
      setError(e?.response?.data?.error || 'No se pudo reactivar la campaña.');
    } finally {
      setGuardando(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50">
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Cabecera */}
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <RotateCcw className="w-4 h-4 text-emerald-600" />
            <h2 className="text-base font-semibold text-slate-900">
              Reactivar campaña
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 p-1"
            aria-label="Cerrar"
            disabled={guardando}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Cuerpo */}
        <div className="px-5 py-4 space-y-4">

          <div className="bg-slate-50 rounded-md p-3 text-sm">
            <div className="font-medium text-slate-900">{campana.titulo}</div>
            <div className="text-xs text-slate-500 mt-0.5">
              Volverá al estado <strong>Activa</strong>.
            </div>
          </div>

          {/* Aviso fecha pasada */}
          {fechaLimitePasada && (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2.5 flex items-start gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <div>
                <strong>La fecha límite ya ha pasado.</strong> La campaña se
                reactivará, pero necesitarás actualizar las fechas después
                para que los usuarios puedan reservar.
              </div>
            </div>
          )}

          {/* Info + radio revivir participaciones */}
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-2">
              Participaciones canceladas por el sistema
            </label>

            <div className="bg-slate-50 rounded-md p-3 mb-3 text-xs text-slate-600 flex items-start gap-2">
              <Users className="w-3.5 h-3.5 shrink-0 mt-0.5 text-slate-400" />
              <div>
                Al cancelar la campaña, las participaciones de usuarios que
                aún no habían reservado quedaron marcadas como{' '}
                <em>canceladas por el sistema</em>.{' '}
                {loadingCount ? (
                  <span className="inline-flex items-center gap-1">
                    <Loader2 className="w-3 h-3 animate-spin" /> Contando...
                  </span>
                ) : nCanceladas !== null ? (
                  <>Hay <strong className="text-slate-900">{nCanceladas}</strong>{' '}
                  {nCanceladas === 1 ? 'participación' : 'participaciones'} en ese estado.</>
                ) : (
                  <>Puede haber participaciones en ese estado.</>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <RadioOpcion
                checked={revivir === 'si'}
                onClick={() => setRevivir('si')}
                label="Restaurar participaciones canceladas por el sistema"
                hint="Los usuarios afectados podrán reservar de nuevo. Recomendado."
                disabled={nCanceladas === 0}
              />
              <RadioOpcion
                checked={revivir === 'no'}
                onClick={() => setRevivir('no')}
                label="No restaurar participaciones"
                hint="Los usuarios canceladas quedan fuera. Podrás añadirlos manualmente después si lo deseas."
              />
            </div>

            {nCanceladas === 0 && !loadingCount && (
              <p className="text-[11px] text-slate-500 mt-2">
                No hay participaciones canceladas por el sistema — la elección
                es irrelevante.
              </p>
            )}
          </div>

          {error && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-100 rounded-md p-2.5 flex items-start gap-1.5">
              <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="text-sm text-slate-500 hover:text-slate-900 px-3 py-1.5"
            disabled={guardando}
          >
            Cancelar
          </button>
          <Button onClick={handleReactivar} disabled={guardando}>
            {guardando ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Reactivando...
              </>
            ) : (
              <>
                <RotateCcw className="w-3.5 h-3.5" />
                Reactivar
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

function RadioOpcion({ checked, onClick, label, hint, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "w-full text-left flex items-start gap-2.5 px-3 py-2 rounded-md border transition-colors",
        checked && !disabled && "border-primary bg-primary/5",
        !checked && !disabled && "border-slate-200 hover:border-slate-300",
        disabled && "border-slate-100 bg-slate-50 cursor-not-allowed opacity-60"
      )}
    >
      <span className={cn(
        "shrink-0 w-3.5 h-3.5 rounded-full border-2 mt-0.5",
        checked && !disabled ? "border-primary bg-primary" : "border-slate-300"
      )}>
        {checked && !disabled && (
          <span className="block w-1 h-1 rounded-full bg-white mx-auto mt-[3px]" />
        )}
      </span>
      <div className="flex-1 min-w-0">
        <div className={cn(
          "text-sm",
          checked && !disabled ? "text-slate-900 font-medium" : "text-slate-700"
        )}>
          {label}
        </div>
        {hint && <div className="text-[11px] text-slate-500 mt-0.5">{hint}</div>}
      </div>
    </button>
  );
}
